import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  TrendingUp, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  Image,
  BookOpen,
  Layout
} from 'lucide-react';
import { useState } from 'react';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/properties', icon: Building2, label: 'Имоти' },
    { path: '/admin/news', icon: BookOpen, label: 'Анализи & Новини' },
    { path: '/admin/leads', icon: Users, label: 'Лидове' },
    { path: '/admin/insights', icon: TrendingUp, label: 'Market Briefs' },
    { path: '/admin/media', icon: Image, label: 'Media Library' },
    { path: '/admin/pages', icon: BookOpen, label: 'Страници' },
    { path: '/admin/content', icon: FileText, label: 'Съдържание' },
    { 
      path: '/admin/page-builder/home', 
      icon: Layout, 
      label: 'Page Builder',
      submenu: [
        { path: '/admin/page-builder/home', label: 'Начална' },
        { path: '/admin/page-builder/about', label: 'За нас' },
        { path: '/admin/page-builder/services', label: 'Услуги' },
        { path: '/admin/page-builder/contact', label: 'Контакти' }
      ]
    },
    { path: '/admin/settings', icon: Settings, label: 'Настройки' }
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#111111] border-r border-[#D4AF37]/20 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-[#D4AF37]/20">
          <img src="/images/deus-logo.png" alt="Deus Estate" className="h-12" />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-[#A9A9A9] hover:text-[#D4AF37]"
          >
            <X size={24} />
          </button>
        </div>

        {/* User Info */}
        <div className="p-6 border-b border-[#D4AF37]/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B8892E] rounded-full flex items-center justify-center">
              <span className="text-[#0B0B0B] font-semibold">
                {user?.full_name?.charAt(0) || 'A'}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-[#F6F2E8]">{user?.full_name}</p>
              <p className="text-xs text-[#D4AF37]">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                  isActive
                    ? 'bg-[#D4AF37] text-[#0B0B0B]'
                    : 'text-[#A9A9A9] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]'
                }`
              }
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#D4AF37]/10">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 w-full rounded-lg text-[#A9A9A9] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors duration-200"
          >
            <LogOut size={20} />
            <span className="font-medium">Изход</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-20 bg-[#111111] border-b border-[#D4AF37]/20 flex items-center justify-between px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-[#A9A9A9] hover:text-[#D4AF37]"
          >
            <Menu size={24} />
          </button>
          
          <div className="flex-1 lg:flex-none">
            <h1 className="text-xl font-serif font-semibold text-[#F6F2E8]">
              Admin Panel
            </h1>
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <span className="text-sm text-[#A9A9A9]">{user?.email}</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
